/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { databaseService } from '@/services/database/database.service.js'
import { seedGroups } from './seeders/group.seeder.js'
import { seedGoats } from './seeders/goat.seeder.js'
import { seedWeights } from './seeders/weight.seeder.js'
import { seedTemperatures } from './seeders/temperature.seeder.js'
import { seedFamacha } from './seeders/famacha.seeder.js'
import { seedDag } from './seeders/dag.seeder.js'
import { seedHealth } from './seeders/health.seeder.js'

/**
 * Database Seeding Orchestrator
 * Controls full seeding for development and debugging
 */

/**
 * Clear the database (child tables first)
 */
async function clearDatabase() {
  console.log('Clearing database...')

  try {
    // Delete health_records first (child of goats)
    await databaseService.run('DELETE FROM health_records', [])
    console.log('  Cleared health_records table')
    // Delete temperatures (child of goats)
    await databaseService.run('DELETE FROM temperatures', [])
    console.log('  Cleared temperatures table')
    // Delete famacha (child of goats)
    await databaseService.run('DELETE FROM famacha', [])
    console.log('  Cleared famacha table')
    // Delete dag (child of goats)
    await databaseService.run('DELETE FROM dag', [])
    console.log('  Cleared dag table')

    // Delete weights second (child of goats)
    await databaseService.run('DELETE FROM weights', [])
    console.log('  Cleared weights table')

    // Delete goats third
    await databaseService.run('DELETE FROM goats', [])
    console.log('  Cleared goats table')

    // Delete groups last (was "herds" before refactor)
    await databaseService.run('DELETE FROM groups', [])
    console.log('  Cleared groups table')

    console.log('Database cleared')
  } catch (error) {
    console.error('Failed to clear database:', error)
    throw error
  }
}

/**
 * Seed all data (groups → goats → weights → temperatures → famacha → dag → health)
 * @param {Object} options
 * @param {number} options.goatCount - Number of goats to generate
 * @param {number} options.weightsPerGoat - Weight records per goat
 * @param {number} options.temperaturesPerGoat - Temperature records per goat
 * @param {number} options.famachaPerGoat - FAMACHA checks per goat
 * @param {number} options.dagPerGoat - DAG checks per goat
 * @param {number} options.healthPerGoat - Health records per goat
 * @param {boolean} options.clearFirst - Clear DB before seeding
 */
export async function seedAll(options = {}) {
  const {
    goatCount = 20,
    weightsPerGoat = 5,
    temperaturesPerGoat = 5,
    famachaPerGoat = 3,
    dagPerGoat = 3,
    healthPerGoat = 8,
    clearFirst = true
  } = options

  console.log('Starting database seeding...')
  console.log(`Platform: ${databaseService.getPlatform()}`)
  console.log(`Adapter: ${databaseService.getAdapterName()}`)
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')

  try {
    // Step 1: Clear database
    if (clearFirst) {
      await clearDatabase()
    }

    // Step 2: Seed groups
    const groupIds = await seedGroups()

    // Step 3: Seed goats
    const goatIds = await seedGoats(groupIds, goatCount)

    // Get goat IDs for seeding health checks
    const goatsResult = await databaseService.query('SELECT id FROM goats', [])
    const goatsData = goatsResult.values.map(g => ({ id: g.id }))

    // Step 4: Seed weights
    await seedWeights(goatIds, weightsPerGoat)

    // Step 5: Seed temperatures
    await seedTemperatures(goatIds, temperaturesPerGoat)

    // Step 6: Seed FAMACHA checks
    await seedFamacha(goatsData, famachaPerGoat)

    // Step 7: Seed DAG checks
    await seedDag(goatsData, dagPerGoat)

    // Step 8: Seed Health records
    await seedHealth(goatIds, healthPerGoat)

    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━')
    console.log('Database seeding complete!')
    console.log(`   Groups:       ${groupIds.length}`)
    console.log(`   Goats:        ${goatCount}`)
    console.log(`   Weights:      ~${goatCount * weightsPerGoat}`)
    console.log(`   Temperatures: ~${goatCount * temperaturesPerGoat}`)
    console.log(`   FAMACHA:      ~${goatCount * famachaPerGoat}`)
    console.log(`   DAG:          ~${goatCount * dagPerGoat}`)
    console.log(`   Health:       ~${goatCount * healthPerGoat}`)
    console.log('Refresh the app to see the data')

    return { groupIds, goatIds, goatCount, weightsPerGoat, temperaturesPerGoat }
  } catch (error) {
    console.error('Seeding failed:', error)
    throw error
  }
}

/**
 * Seed only groups
 */
export async function seedOnlyGroups() {
  console.log('Seeding groups only...')
  try {
    const groupIds = await seedGroups()
    console.log(`Seeded ${groupIds.length} groups`)
    return groupIds
  } catch (error) {
    console.error('Failed to seed groups:', error)
    throw error
  }
}

/**
 * Seed only goats
 */
export async function seedOnlyGoats(count = 20) {
  console.log('Seeding goats only...')

  try {
    // Get group IDs for assignment
    const result = await databaseService.query('SELECT id FROM groups', [])
    const groupIds = result.values.map(g => g.id)

    const goatIds = await seedGoats(groupIds, count)
    console.log(`Seeded ${goatIds.length} goats`)

    return goatIds
  } catch (error) {
    console.error('Failed to seed goats:', error)
    throw error
  }
}

/**
 * Seed only weights for existing goats
 */
export async function seedOnlyWeights(weightsPerGoat = 5) {
  console.log('Seeding weights only...')

  try {
    // Get existing goat IDs
    const result = await databaseService.query('SELECT id FROM goats', [])
    const goatIds = result.values.map(g => g.id)

    await seedWeights(goatIds, weightsPerGoat)
    console.log(`Seeded ~${goatIds.length * weightsPerGoat} weights`)

    return goatIds
  } catch (error) {
    console.error('Failed to seed weights:', error)
    throw error
  }
}

/**
 * Seed only temperatures for existing goats
 */
export async function seedOnlyTemperatures(temperaturesPerGoat = 5) {
  console.log('Seeding temperatures only...')

  try {
    // Get existing goat IDs
    const result = await databaseService.query('SELECT id FROM goats', [])
    const goatIds = result.values.map(g => g.id)

    await seedTemperatures(goatIds, temperaturesPerGoat)
    console.log(`Seeded ~${goatIds.length * temperaturesPerGoat} temperatures`)

    return goatIds
  } catch (error) {
    console.error('Failed to seed temperatures:', error)
    throw error
  }
}
/**
 * Seed only FAMACHA checks for existing goats
 */
export async function seedOnlyFamacha(famachaPerGoat = 3) {
  console.log('Seeding FAMACHA checks only...')

  try {
    // Get existing goats
    const result = await databaseService.query('SELECT id FROM goats', [])
    const goatsData = result.values.map(g => ({ id: g.id }))

    await seedFamacha(goatsData, famachaPerGoat)
    console.log(`Seeded ~${goatsData.length * famachaPerGoat} FAMACHA checks`)

    return goatsData
  } catch (error) {
    console.error('Failed to seed FAMACHA checks:', error)
    throw error
  }
}

/**
 * Seed only DAG checks for existing goats
 */
export async function seedOnlyDag(dagPerGoat = 3) {
  console.log('Seeding DAG checks only...')

  try {
    // Get existing goats
    const result = await databaseService.query('SELECT id FROM goats', [])
    const goatsData = result.values.map(g => ({ id: g.id }))

    await seedDag(goatsData, dagPerGoat)
    console.log(`Seeded ~${goatsData.length * dagPerGoat} DAG checks`)

    return goatsData
  } catch (error) {
    console.error('Failed to seed DAG checks:', error)
    throw error
  }
}

/**
 * Seed only health records for existing goats
 */
export async function seedOnlyHealth(healthPerGoat = 8) {
  console.log('Seeding health records only...')

  try {
    // Get existing goat IDs
    const result = await databaseService.query('SELECT id FROM goats', [])
    const goatIds = result.values.map(g => g.id)

    await seedHealth(goatIds, healthPerGoat)
    console.log(`Seeded ~${goatIds.length * healthPerGoat} health records`)

    return goatIds
  } catch (error) {
    console.error('Failed to seed health records:', error)
    throw error
  }
}

export default {
  seedAll,
  seedOnlyGroups,
  seedOnlyGoats,
  seedOnlyWeights,
  seedOnlyTemperatures,
  seedOnlyFamacha,
  seedOnlyDag,
  seedOnlyHealth
}
